import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, User as UserIcon, Shield, Save, Trash2, Check, EyeOff } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { ConfirmModal } from '@onvice/portal-ui';
import { getModuleLabel } from '@onvice/portal-ui';
import { NAV_ITEMS } from '../../components/PortalSidebar';
import { refreshSidebarBlacklist } from '../../lib/useSidebarBlacklist';

type Level = 'admin' | 'editor' | 'author' | 'tester' | 'reader' | 'depositor' | 'none';

const LEVELS: Level[] = ['none', 'reader', 'tester', 'author', 'editor', 'admin'];

interface GroupDetail {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  members: { id: string; first_name: string; last_name: string; email: string }[];
  subgroups: { id: string; name: string }[];
}

interface ModuleAccessRow {
  module_slug: string;
  level: Level;
}

interface PersonOpt { id: string; first_name: string; last_name: string; email: string }

const LEVEL_COLOR: Record<Level, string> = {
  admin:    'bg-brand-500/20 text-brand-300 border-brand-500/30',
  editor:   'bg-sky-500/20 text-sky-300 border-sky-500/30',
  author:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  tester:   'bg-purple-500/20 text-purple-300 border-purple-500/30',
  reader:   'bg-slate-500/20 text-slate-300 border-slate-500/30',
  depositor:'bg-amber-500/20 text-amber-300 border-amber-500/30',
  none:     'bg-slate-800 text-slate-500 border-slate-700',
};

export default function AdminAdressbuchGroupDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [access, setAccess] = useState<ModuleAccessRow[]>([]);
  const [allPersons, setAllPersons] = useState<PersonOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // SP1 2026-07-21: Gruppen-Sidebar-Blacklist
  const [blacklist, setBlacklist] = useState<Set<string>>(new Set());

  function showSaved(label: string) {
    setSavedHint(label);
    setTimeout(() => setSavedHint(null), 1500);
  }

  function load() {
    if (!id) return;
    setLoading(true);
    Promise.all([
      api.get<{ success: boolean; data: GroupDetail }>(`/admin/adressbuch/groups/${id}`),
      api.get<{ success: boolean; data: ModuleAccessRow[] }>(`/admin/adressbuch/groups/${id}/access`),
      api.get<{ success: boolean; data: PersonOpt[] }>(`/admin/adressbuch/persons`),
      api.get<{ success: boolean; data: { slugs: string[] } }>(`/admin/adressbuch/groups/${id}/sidebar-blacklist`),
    ])
      .then(([g, a, p, b]) => {
        setGroup(g.data); setAccess(a.data); setAllPersons(p.data);
        setBlacklist(new Set(b.data.slugs ?? []));
      })
      .catch((err) => setError(err.message ?? 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }
  useEffect(load, [id]);

  async function saveBlacklist(nextSlugs: Set<string>) {
    if (!id) return;
    setSaving('blacklist');
    try {
      await api.put(`/admin/adressbuch/groups/${id}/sidebar-blacklist`, { slugs: Array.from(nextSlugs) });
      setBlacklist(nextSlugs);
      // Der Admin ist meist selbst in keiner der geaenderten Gruppen (Wildcard-
      // Bypass) — aber wenn doch, soll AssistantAvatar / MobileHomeTabBar
      // sofort neu evaluieren statt bis zum naechsten Login zu warten.
      refreshSidebarBlacklist();
      showSaved('Sidebar-Blacklist');
    } finally {
      setSaving(null);
    }
  }
  function toggleBlacklistSlug(slug: string) {
    const next = new Set(blacklist);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    void saveBlacklist(next);
  }

  async function saveStammdaten(name: string, description: string) {
    if (!id) return;
    setSaving('stammdaten');
    try {
      await api.put(`/admin/adressbuch/groups/${id}`, { name, description: description || null });
      showSaved('Stammdaten');
      load();
    } finally {
      setSaving(null);
    }
  }

  async function saveMembers(personIds: string[]) {
    if (!id) return;
    setSaving('members');
    try {
      await api.put(`/admin/adressbuch/groups/${id}/members`, { personIds });
      showSaved('Mitglieder');
      load();
    } finally {
      setSaving(null);
    }
  }

  async function saveAccess(moduleSlug: string, level: Level) {
    if (!id) return;
    setSaving(`access:${moduleSlug}`);
    try {
      await api.put(`/admin/adressbuch/modules/${moduleSlug}/access/group/${id}`, { level });
      setAccess((prev) => prev.map((r) => (r.module_slug === moduleSlug ? { ...r, level } : r)));
      showSaved(`${moduleSlug} = ${level}`);
    } catch (err) {
      setError((err as Error).message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSaving(null);
    }
  }

  async function doDelete() {
    if (!id) return;
    setSaving('delete');
    try {
      await api.delete(`/admin/adressbuch/groups/${id}`);
      navigate('/admin/adressbuch');
    } catch (err) {
      setError((err as Error).message ?? 'Loeschen fehlgeschlagen');
    } finally {
      setSaving(null);
      setConfirmDelete(false);
    }
  }

  if (loading && !group) return <div className="px-4 sm:px-8 py-6 text-slate-400">Laden…</div>;
  if (error) return <div className="px-4 sm:px-8 py-6 text-red-400">{error}</div>;
  if (!group) return null;

  return (
    <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/adressbuch" className="text-slate-400 hover:text-slate-200 -ml-2 p-2">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Users size={20} /> {group.name}
        </h1>
        <span className="text-xs text-muted-foreground ml-auto">
          {group.members.length} {group.members.length === 1 ? 'Person' : 'Personen'}
          {group.subgroups.length > 0 && ` + ${group.subgroups.length} Untergruppen`}
        </span>
        {savedHint && (
          <span className="flex items-center gap-1 text-[12px] text-brand-400">
            <Check size={14} /> {savedHint} gespeichert
          </span>
        )}
      </div>

      <StammdatenSection
        group={group}
        saving={saving === 'stammdaten'}
        onSave={saveStammdaten}
        onRequestDelete={() => setConfirmDelete(true)}
      />

      <MembersSection
        group={group}
        allPersons={allPersons}
        saving={saving === 'members'}
        onSave={saveMembers}
      />

      {/* 2026-07-07 (Joerg): Modul-Levels der Gruppe direkt hier editierbar
          (statt Umweg ueber die Modul-Detail-Page). Aendert PUT
          /admin/adressbuch/modules/:slug/access/group/:id — dieselbe API wie im
          Modul-Editor, nur andere UI-Perspektive. */}
      <section className="bg-card border border-default rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Shield size={14} /> Modul-Zugriff dieser Gruppe
        </h2>
        <p className="text-[11px] text-slate-500 mb-3">
          Level pro Modul einstellen — Personen dieser Gruppe erben den Level automatisch.
          Verweis auf die Modul-Detail-Seite: dort sieht man umgekehrt alle Gruppen + Personen pro Modul.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {access.map((m) => (
            <div
              key={m.module_slug}
              className="flex items-center justify-between gap-2 border border-default rounded-md px-3 py-2"
            >
              <Link
                to={`/admin/adressbuch/modules/${m.module_slug}`}
                className="flex-1 min-w-0 flex items-center gap-2 hover:text-primary"
                title="Zur Modul-Detail-Seite"
              >
                <span className="text-sm font-medium text-foreground truncate">{getModuleLabel(m.module_slug)}</span>
                <span className="font-mono text-[11px] text-muted-foreground truncate">{m.module_slug}</span>
              </Link>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${LEVEL_COLOR[m.level]}`}>
                {m.level}
              </span>
              <select
                value={m.level}
                disabled={saving === `access:${m.module_slug}`}
                onChange={(e) => saveAccess(m.module_slug, e.target.value as Level)}
                className="h-8 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-[12px] disabled:opacity-40"
                aria-label={`Level fuer ${m.module_slug}`}
              >
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          ))}
        </div>
      </section>

      {/* SP1 2026-07-21: Sidebar-Blacklist pro Gruppe. Admin blendet Sidebar-
          Eintraege aus, damit Portal beim Kunden schrittweise eingefuehrt werden
          kann. ACL bleibt unveraendert — nur die UI-Sichtbarkeit wird gesteuert.
          Admin+GF (Wildcard) sehen die Blacklist nicht — sie behalten Vollsicht.
          Wirkt tenant-weit (Desktop-Sidebar, Mobile-Tabs, Mobile-Mehr, Home). */}
      <section className="bg-card border border-default rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <EyeOff size={14} /> Sidebar-Sichtbarkeit dieser Gruppe
        </h2>
        <p className="text-[11px] text-slate-500 mb-3">
          Angehakte Punkte werden fuer Mitglieder dieser Gruppe <b>ausgeblendet</b>,
          selbst wenn die ACL Zugriff erlaubt. Admin und GF sehen immer alles.
          Ideal fuer Rollout in Etappen — Modul-Zugriff aktiv lassen, Sidebar-
          Punkt spaeter freigeben.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {[
            ...NAV_ITEMS.filter((it) => it.slug !== 'home').map((it) => ({ slug: it.slug, label: it.label })),
            // 2026-08-20: Zusatz-Slugs die kein NAV_ITEMS-Eintrag sind, aber
            // ausserhalb der klassischen Sidebar erreichbar. Der Consumer
            // (AssistantAvatar, MobileHomeTabBar.MORE_ITEMS) filtert selbst
            // gegen die gleiche Blacklist-Union.
            { slug: 'assistant', label: 'Assistent (Avatar oben rechts + Mobile-Mehr)' },
          ].map((it) => {
            const hidden = blacklist.has(it.slug);
            return (
              <label
                key={it.slug}
                className={`flex items-center gap-2 border rounded-md px-2 py-1.5 cursor-pointer text-xs ${
                  hidden
                    ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                    : 'border-default hover:border-primary/40 text-foreground'
                }`}
                title={hidden ? 'Ausgeblendet — klicken zum Anzeigen' : 'Sichtbar — klicken zum Ausblenden'}
              >
                <input
                  type="checkbox"
                  checked={hidden}
                  disabled={saving === 'blacklist'}
                  onChange={() => toggleBlacklistSlug(it.slug)}
                  className="flex-shrink-0"
                />
                <span className="truncate">{it.label}</span>
              </label>
            );
          })}
        </div>
        {blacklist.size === 0 && (
          <div className="mt-2 text-[11px] text-emerald-400/80">
            Keine Einschraenkungen — Gruppe sieht alle laut ACL erlaubten Sidebar-Punkte.
          </div>
        )}
      </section>

      <ConfirmModal
        open={confirmDelete}
        title="Gruppe löschen?"
        message={`Die Gruppe "${group.name}" wird endgültig gelöscht. ACL-Einträge dieser Gruppe in allen Modulen werden mit entfernt. Mitgliedschaften (${group.members.length} Personen) werden gelöst.`}
        confirmLabel="Endgültig löschen"
        danger
        busy={saving === 'delete'}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}

function StammdatenSection({ group, saving, onSave, onRequestDelete }: {
  group: GroupDetail;
  saving: boolean;
  onSave: (name: string, description: string) => Promise<void>;
  onRequestDelete: () => void;
}) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  useEffect(() => { setName(group.name); setDescription(group.description ?? ''); }, [group]);
  const dirty = name !== group.name || description !== (group.description ?? '');
  return (
    <section className="bg-card border border-default rounded-lg p-4 space-y-3">
      <h2 className="text-sm font-semibold text-slate-300">Stammdaten</h2>
      <label className="block text-sm">
        <span className="text-slate-400 text-[12px]">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="text-slate-400 text-[12px]">Beschreibung</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="mt-1 w-full px-2 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
        />
      </label>
      <div className="flex justify-between items-center pt-1">
        <button
          onClick={onRequestDelete}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-rose-400 hover:bg-rose-500/10 text-sm"
        >
          <Trash2 size={14} /> Gruppe löschen
        </button>
        <button
          disabled={saving || !dirty || !name.trim()}
          onClick={() => onSave(name.trim(), description)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground text-sm"
        >
          <Save size={14} /> {saving ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </section>
  );
}

function MembersSection({ group, allPersons, saving, onSave }: {
  group: GroupDetail;
  allPersons: PersonOpt[];
  saving: boolean;
  onSave: (personIds: string[]) => Promise<void>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(group.members.map((m) => m.id)));
  const [filter, setFilter] = useState('');
  useEffect(() => {
    setSelected(new Set(group.members.map((m) => m.id)));
  }, [group]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const dirty = selected.size !== group.members.length
    || group.members.some((m) => !selected.has(m.id));
  const q = filter.trim().toLowerCase();
  const visible = q
    ? allPersons.filter((p) =>
        p.first_name.toLowerCase().includes(q) ||
        p.last_name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q))
    : allPersons;

  return (
    <section className="bg-card border border-default rounded-lg p-4">
      <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
        <UserIcon size={14} /> Mitglieder
      </h2>
      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Personen filtern…"
        className="w-full h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm mb-3"
      />
      <div className="max-h-96 overflow-y-auto space-y-1 mb-3">
        {visible.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-2">Keine Treffer</p>
        ) : (
          visible.map((p) => (
            <label key={p.id} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md border cursor-pointer ${selected.has(p.id) ? 'border-brand-500/40 bg-brand-500/5' : 'border-default hover:border-slate-600'}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="rounded" />
              <span className="flex-1">{p.first_name} {p.last_name}</span>
              <span className="text-[11px] text-slate-500">{p.email}</span>
            </label>
          ))
        )}
      </div>
      {group.subgroups.length > 0 && (
        <>
          <h3 className="text-[12px] font-semibold text-slate-400 mt-3 mb-2">Untergruppen</h3>
          <div className="flex flex-wrap gap-1.5">
            {group.subgroups.map((sg) => (
              <Link
                key={sg.id}
                to={`/admin/adressbuch/groups/${sg.id}`}
                className="text-[12px] px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-300 border border-brand-500/20 hover:bg-brand-500/25"
              >
                {sg.name}
              </Link>
            ))}
          </div>
        </>
      )}
      <div className="flex justify-end mt-3">
        <button
          disabled={saving || !dirty}
          onClick={() => onSave(Array.from(selected))}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground text-sm"
        >
          <Save size={14} /> {saving ? 'Speichern…' : 'Mitglieder speichern'}
        </button>
      </div>
    </section>
  );
}
